import React from 'react';

import { useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import {
    Button,
    Glyphicon,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { ndsInsuranceSponsorCtaClick } from 'reaxl-analytics-handlers';

import { inventoryDuck, userDuck } from '@/ducks';

const logoMap = {
    root: 'root_ins_logo.svg',
};

// TODO: Remove after Root sponsorship is done, this is only available for users/dealers in these states
const eligibleStates = ['AL', 'AZ', 'AR', 'CO', 'DE', 'GA', 'IN', 'IA', 'IL', 'KS', 'KY', 'LA', 'MD', 'MN', 'MS', 'MO', 'NE', 'NM', 'OH', 'OK', 'OR', 'PA', 'SC', 'TN', 'TX', 'UT', 'VA', 'WV', 'WI'];

const getIsStateEligible = (userState, dealerState) => {
    if (userState) {
        return eligibleStates?.includes(userState);
    }

    return eligibleStates?.includes(dealerState);
};

const SponsoredInsuranceContainer = React.memo(({
    insurancePartnerEnabled,
    id: listingId,
    vin,
    zip,
}) => {

    const {
        nds_sponsored_insurance: [, {
            copy,
            external_host: externalHost,
            zip_param: zipParam,
            vin_param: vinParam,
            sponsor_name: sponsorName,
        }],
    } = useFeatures(['nds_sponsored_insurance']);

    const userLocation = useSelector(userDuck.selectors.getLocation);

    const inventory = useSelector(inventoryDuck.selectors.selectHydratedInventory);
    const owner = inventory[listingId]?.owner;
    const ownerLocation = owner?.location?.address;
    const ownerId = owner?.id;

    const eventData = {
        listingId,
        sponsor: sponsorName,
        ownerId,
        zip,
    };

    const handleImpression = () => {
        sendImpressions({ name: 'ndsInsuranceSponsorInviewImpression', data: { ...eventData } });
    };

    const buildHref = () => {
        let host = `${externalHost}&${vinParam}=${vin}`;

        if (zipParam && zip) {
            host += `&${zipParam}=${zip}`;
        }

        return host;
    };

    const ctaHref = buildHref();

    const handleCtaClick = (event) => {
        sendClick(ndsInsuranceSponsorCtaClick, event, { ...eventData });
    };

    const isStateEligible = getIsStateEligible(userLocation?.state, ownerLocation?.state);

    const ctaCopy = copy || 'Get insurance quotes for this vehicle';

    const showInsurancePartner = insurancePartnerEnabled && isStateEligible;

    return !!showInsurancePartner && (
        <DelayedImpressionObserver
            key={`sponsored-insurance-container-${vin}`}
            onChange={handleImpression}
            delay={0}
            id="sponsored-insurance-container"
        >
            <div
                className="display-flex justify-content-start align-items-center margin-top-5"
                data-cmp="sponsored-insurance-container"
            >
                <img
                    height="20px"
                    width="80px"
                    src={`https://www.autotrader.com/content/static/img/icon/logos/${logoMap[sponsorName?.toLowerCase()]}`}
                    alt={`${sponsorName} Logo`}
                    className="margin-right-2 margin-right-sm-3"
                />

                <div
                    className="text-bold text-size-200 display-flex align-items-center align-self-stretch padding-horizontal-sm-3 padding-left-2"
                    style={{ borderLeft: '1px solid currentColor' }}
                >
                    {ctaCopy}
                </div>

                <Button
                    bsStyle="default"
                    bsSize="small"
                    className="margin-left-2 margin-left-sm-0"
                    onClick={handleCtaClick}
                    href={ctaHref}
                    target="_blank"
                >
                    Get a Quote
                    <Glyphicon
                        glyph="offsite"
                        className="margin-left-1"
                    />
                </Button>
            </div>
        </DelayedImpressionObserver>
    );
}, (prevProps, nextProps) => prevProps.vin === nextProps.vin && prevProps.zip === nextProps.zip);

export default SponsoredInsuranceContainer;
